import { useState, useEffect } from 'react';
import { accountService, revenueService } from '../../services/api';
import {
  fetchMergedRevenueTransactions,
  augmentOverviewFromTransactions,
  computeRevenueSummaryFromTransactions,
} from '../utils/orderRevenueTransactions';
function syncRevenueFromTransactions(prev, transactions, patch = {}) {
  const overview = augmentOverviewFromTransactions(prev.overview, transactions);
  return {
    ...prev,
    ...patch,
    transactions,
    overview,
    codRealIncome: overview.codRealIncome ?? 0,
    codPendingIncome: overview.codPendingIncome ?? 0,
    codTotalIncome: overview.codTotalIncome ?? 0,
  };
}

const useRevenue = ({ creatorScopeId = '' } = {}) => {
  const scopedCreatorId =
    creatorScopeId != null && String(creatorScopeId).trim()
      ? String(creatorScopeId).trim()
      : '';
  const [revenueData, setRevenueData] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    weeklyRevenue: 0,
    dailyRevenue: 0,
    transactions: [],
    accounts: [],
    overview: null,
    stats: null,
    codPendingIncome: 0,
    codRealIncome: 0,
    codTotalIncome: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRevenueStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await revenueService.getStats();
      
      if (result.success) {
        const stats = result.data;
        setRevenueData(prev => ({
          ...prev,
          totalRevenue: stats.allTime?.revenue || 0,
          monthlyRevenue: stats.thisMonth?.revenue || 0,
          weeklyRevenue: Math.round((stats.thisMonth?.revenue || 0) / 4),
          dailyRevenue: stats.today?.revenue || 0,
          stats: stats
        }));
      } else {
        throw new Error(result.error || result.message || 'Failed to fetch revenue stats');
      }
    } catch (error) {
      console.error('Revenue stats error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueOverview = async (startDate, endDate) => {
    try {
      setLoading(true);
      setError(null);

      const result = await revenueService.getOverview(startDate, endDate);
      
      if (result.success) {
        setRevenueData(prev => ({ ...prev, overview: result.data }));
      } else {
        throw new Error(result.error || result.message || 'Failed to fetch revenue overview');
      }
    } catch (error) {
      console.error('Revenue overview error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenueTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      let accounts = revenueData.accounts;
      if (!accounts?.length) {
        try {
          const accResult = await accountService.getAccounts();
          accounts = accResult.success ? accResult.data || [] : [];
        } catch {
          accounts = [];
        }
      }
      const transactions = await fetchMergedRevenueTransactions(accounts, {
        creatorId: scopedCreatorId || undefined,
      });
      setRevenueData((prev) =>
        syncRevenueFromTransactions(prev, transactions, {
          ...(accounts.length && !prev.accounts?.length ? { accounts } : {}),
        })
      );
    } catch (error) {
      console.error('Revenue transactions error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentAccounts = async (filters = {}) => {
    if (scopedCreatorId) {
      try {
        setLoading(true);
        setError(null);
        const transactions = await fetchMergedRevenueTransactions([], {
          creatorId: scopedCreatorId,
        });
        setRevenueData((prev) =>
          syncRevenueFromTransactions(prev, transactions, { accounts: [] })
        );
      } catch (error) {
        console.error('Creator-scoped revenue error:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await accountService.getAccounts(filters);
      
      if (result.success) {
        const accounts = result.data || [];
        
        // Calculate income for each account based on transactions
        const today = new Date();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        
        // Merged API + confirmed-order transactions (same rules as Team Sales)
        try {
          const transactions = await fetchMergedRevenueTransactions(accounts, {
            creatorId: scopedCreatorId || undefined,
          });
          
          // Calculate income per account
          const accountsWithIncome = accounts.map(account => {
            const accountIdStr = String(account._id || account.id || '').trim();
            const accountNameStr = String(account.name || '').toLowerCase().trim();
            
            // Match transactions by paymentAccount (new structure), accountId, or accountName
            const accountTransactions = transactions.filter(t => {
              // Get transaction account identifiers (check multiple possible fields)
              const tPaymentAccountId = t.paymentAccount?._id || t.paymentAccount || null;
              const tPaymentAccountName = t.paymentAccount?.name ? String(t.paymentAccount.name).toLowerCase().trim() : null;
              const tAccountId = String(t.accountId || '').trim();
              const tAccountName = String(t.accountName || '').toLowerCase().trim();
              
              // Primary match: paymentAccount._id (new structure - most reliable)
              if (tPaymentAccountId && accountIdStr && String(tPaymentAccountId).trim() === accountIdStr) {
                return true;
              }
              
              // Secondary match: paymentAccount.name (new structure)
              if (tPaymentAccountName && accountNameStr && tPaymentAccountName === accountNameStr) {
                return true;
              }
              
              // Tertiary match: accountId (legacy field)
              if (tAccountId && accountIdStr && tAccountId === accountIdStr) {
                return true;
              }
              
              // Fourth match: accountName (legacy field)
              if (tAccountName && accountNameStr && tAccountName === accountNameStr) {
                return true;
              }
              
              // Fallback: check if source matches account name (for old transactions only)
              // Note: This should not be used for new transactions as source is now an enum
              if (t.source && String(t.source).toLowerCase().trim() === accountNameStr) {
                console.warn('Transaction matched by source (legacy):', {
                  transactionId: t._id,
                  source: t.source,
                  accountName: accountNameStr,
                  accountId: accountIdStr
                });
                return true;
              }
              
              return false;
            });
            
            // Debug: Log matching results
            if (accountTransactions.length > 0) {
              console.log(`Account "${account.name}" (ID: ${accountIdStr}):`, {
                totalTransactions: accountTransactions.length,
                matchedBy: accountTransactions.map(t => {
                  const tPaymentAccountId = t.paymentAccount?._id || t.paymentAccount;
                  const tPaymentAccountName = t.paymentAccount?.name ? String(t.paymentAccount.name).toLowerCase() : null;
                  const tAccountId = String(t.accountId || '').trim();
                  const tAccountName = String(t.accountName || '').toLowerCase().trim();
                  
                  if (tPaymentAccountId && String(tPaymentAccountId).trim() === accountIdStr) {
                    return { transactionId: t._id, matchedBy: 'paymentAccount._id' };
                  } else if (tPaymentAccountName === accountNameStr) {
                    return { transactionId: t._id, matchedBy: 'paymentAccount.name' };
                  } else if (tAccountId === accountIdStr) {
                    return { transactionId: t._id, matchedBy: 'accountId' };
                  } else if (tAccountName === accountNameStr) {
                    return { transactionId: t._id, matchedBy: 'accountName' };
                  } else {
                    return { transactionId: t._id, matchedBy: 'source' };
                  }
                })
              });
            }
            
            // Debug: Log account and its transactions
            if (accountTransactions.length > 0) {
              console.log('Account:', account.name, 'Transactions:', accountTransactions.length);
              console.log('Account ID:', account._id || account.id);
              accountTransactions.forEach(t => {
                console.log('Transaction:', {
                  id: t._id,
                  accountId: t.accountId,
                  accountName: t.accountName,
                  type: t.type,
                  amount: t.amount,
                  status: t.status
                });
              });
            }
            
            // Daily income (today) - deposits/income only
            const dailyIncome = accountTransactions
              .filter(t => {
                const tDate = new Date(t.transactionDate || t.createdAt);
                return tDate.toDateString() === today.toDateString() && 
                       (t.type === 'income' || t.type === 'deposit') &&
                       t.status === 'completed';
              })
              .reduce((sum, t) => sum + (t.amount || 0), 0);
            
            // Weekly income (this week) - deposits/income only
            const weeklyIncome = accountTransactions
              .filter(t => {
                const tDate = new Date(t.transactionDate || t.createdAt);
                return tDate >= weekStart && 
                       (t.type === 'income' || t.type === 'deposit') &&
                       t.status === 'completed';
              })
              .reduce((sum, t) => sum + (t.amount || 0), 0);
            
            // Monthly income (this month) - deposits/income only
            const monthlyIncome = accountTransactions
              .filter(t => {
                const tDate = new Date(t.transactionDate || t.createdAt);
                return tDate >= monthStart && 
                       (t.type === 'income' || t.type === 'deposit') &&
                       t.status === 'completed';
              })
              .reduce((sum, t) => sum + (t.amount || 0), 0);
            
            // Total balance (all time) - deposits/income minus withdrawals/expenses
            const totalIncome = accountTransactions
              .filter(t => {
                // Only include completed transactions
                return t.status === 'completed';
              })
              .reduce((sum, t) => {
                const amount = parseFloat(t.amount) || 0;
                if (t.type === 'income' || t.type === 'deposit') {
                  return sum + amount;
                } else if (t.type === 'withdrawal' || t.type === 'expense') {
                  return sum - amount;
                }
                return sum;
              }, 0);
            
            // Debug: Log calculated balance for troubleshooting
            if (accountTransactions.length > 0) {
              const deposits = accountTransactions.filter(t => 
                (t.type === 'deposit' || t.type === 'income') && t.status === 'completed'
              );
              const withdrawals = accountTransactions.filter(t => 
                (t.type === 'withdrawal' || t.type === 'expense') && t.status === 'completed'
              );
              
              console.log(`Account "${account.name}" (ID: ${account._id || account.id}):`, {
                totalTransactions: accountTransactions.length,
                deposits: deposits.length,
                withdrawals: withdrawals.length,
                depositTotal: deposits.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
                withdrawalTotal: withdrawals.reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0),
                calculatedBalance: totalIncome
              });
            }
            
            // Special logging for Easypaisa (SUPER) account
            if (account.name && (account.name.toLowerCase().includes('easypaisa') || account.name.toLowerCase().includes('super'))) {
              console.log('🔍 EASYPAISA (SUPER) ACCOUNT BALANCE CHECK:', {
                accountName: account.name,
                accountId: account._id || account.id,
                totalIncome: finalTotalIncome,
                totalTransactions: accountTransactions.length,
                transactions: accountTransactions.map(t => ({
                  id: t._id,
                  type: t.type,
                  amount: t.amount,
                  status: t.status,
                  paymentAccount: t.paymentAccount?._id || t.paymentAccount,
                  paymentAccountName: t.paymentAccount?.name,
                  accountId: t.accountId,
                  accountName: t.accountName
                })),
                deposits: accountTransactions.filter(t => (t.type === 'deposit' || t.type === 'income') && t.status === 'completed').map(t => ({ amount: t.amount, date: t.transactionDate || t.createdAt })),
                withdrawals: accountTransactions.filter(t => (t.type === 'withdrawal' || t.type === 'expense') && t.status === 'completed').map(t => ({ amount: t.amount, date: t.transactionDate || t.createdAt }))
              });
            }
            
            // Always set totalIncome, even if 0 (to ensure it's defined)
            const finalTotalIncome = Math.max(0, totalIncome);
            
            // Debug: Log if account has no transactions but should still show 0
            if (accountTransactions.length === 0) {
              console.log(`Account "${account.name}" (ID: ${account._id || account.id}): No transactions found, balance set to 0`);
              
              // Special logging for Easypaisa (SUPER) account with no transactions
              if (account.name && (account.name.toLowerCase().includes('easypaisa') || account.name.toLowerCase().includes('super'))) {
                console.log('🔍 EASYPAISA (SUPER) ACCOUNT - NO TRANSACTIONS FOUND:', {
                  accountName: account.name,
                  accountId: account._id || account.id,
                  balance: 0,
                  allTransactionsCount: transactions.length,
                  sampleTransactions: transactions.slice(0, 5).map(t => ({
                    id: t._id,
                    paymentAccount: t.paymentAccount?._id || t.paymentAccount,
                    paymentAccountName: t.paymentAccount?.name,
                    accountId: t.accountId,
                    accountName: t.accountName,
                    source: t.source
                  }))
                });
              }
            }
            
            return {
              ...account,
              dailyIncome,
              weeklyIncome,
              monthlyIncome,
              totalIncome: finalTotalIncome // Always set, even if 0
            };
          });
          
          // Ensure all accounts have totalIncome set (even if 0)
          const accountsWithIncomeEnsured = accountsWithIncome.map(acc => ({
            ...acc,
            totalIncome: acc.totalIncome !== undefined && acc.totalIncome !== null ? acc.totalIncome : 0
          }));
          
          // Log summary of all accounts with balances
          console.log('📊 ALL ACCOUNTS BALANCE SUMMARY:', accountsWithIncomeEnsured.map(acc => ({
            name: acc.name,
            id: acc._id || acc.id,
            balance: acc.totalIncome,
            paymentMethod: acc.paymentMethod
          })));
          
          // Specifically highlight Easypaisa (SUPER) account
          const easypaisaAccount = accountsWithIncomeEnsured.find(acc => 
            acc.name && (acc.name.toLowerCase().includes('easypaisa') || acc.name.toLowerCase().includes('super'))
          );
          if (easypaisaAccount) {
            console.log('💰 EASYPAISA (SUPER) ACCOUNT BALANCE:', {
              accountName: easypaisaAccount.name,
              accountId: easypaisaAccount._id || easypaisaAccount.id,
              balance: easypaisaAccount.totalIncome,
              formattedBalance: `PKR ${easypaisaAccount.totalIncome.toLocaleString()}`
            });
          } else {
            console.warn('⚠️ Easypaisa (SUPER) account not found in accounts list');
          }
          
          setRevenueData((prev) =>
            syncRevenueFromTransactions(prev, transactions, {
              accounts: accountsWithIncomeEnsured,
            })
          );
        } catch (transError) {
          console.error('Error calculating account income:', transError);
          // Still set accounts even if income calculation fails, but ensure totalIncome is set to 0
          const accountsWithDefaults = accounts.map(acc => ({
            ...acc,
            totalIncome: 0
          }));
          setRevenueData(prev => ({ ...prev, accounts: accountsWithDefaults }));
        }
      } else {
        throw new Error(result.error || result.message || 'Failed to fetch payment accounts');
      }
    } catch (error) {
      console.error('Payment accounts error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Payment Account Management
  const addPaymentAccount = async (accountData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await accountService.createAccount(accountData);
      
      if (result.success) {
        await fetchPaymentAccounts(); // Refresh accounts
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to add payment account');
      }
    } catch (error) {
      console.error('Add payment account error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentAccount = async (accountId, accountData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await accountService.updateAccount(accountId, accountData);
      
      if (result.success) {
        await fetchPaymentAccounts(); // Refresh accounts
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to update payment account');
      }
    } catch (error) {
      console.error('Update payment account error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deletePaymentAccount = async (accountId) => {
    try {
      setLoading(true);
      setError(null);

      const result = await accountService.deleteAccount(accountId);
      
      if (result.success) {
        await fetchPaymentAccounts(); // Refresh accounts
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to delete payment account');
      }
    } catch (error) {
      console.error('Delete payment account error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountVisibility = async (accountId, isVisible) => {
    try {
      setLoading(true);
      setError(null);

      const result = await accountService.toggleVisibility(accountId, isVisible);
      
      if (result.success) {
        await fetchPaymentAccounts(); // Refresh accounts
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to toggle account visibility');
      }
    } catch (error) {
      console.error('Toggle account visibility error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountStatus = async (accountId, isActive) => {
    try {
      setLoading(true);
      setError(null);

      const result = await accountService.toggleStatus(accountId, isActive);
      
      if (result.success) {
        await fetchPaymentAccounts(); // Refresh accounts
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to toggle account status');
      }
    } catch (error) {
      console.error('Toggle account status error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Revenue Transaction Management
  const addRevenueTransaction = async (transactionData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await revenueService.createTransaction(transactionData);
      
      if (result.success) {
        await fetchRevenueTransactions(); // Refresh transactions
        await fetchRevenueStats(); // Refresh stats
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to add revenue transaction');
      }
    } catch (error) {
      console.error('Add revenue transaction error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateRevenueTransaction = async (transactionId, transactionData) => {
    try {
      setLoading(true);
      setError(null);

      const result = await revenueService.updateTransaction(transactionId, transactionData);
      
      if (result.success) {
        await fetchRevenueTransactions(); // Refresh transactions
        await fetchRevenueStats(); // Refresh stats
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to update revenue transaction');
      }
    } catch (error) {
      console.error('Update revenue transaction error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const deleteRevenueTransaction = async (transactionId) => {
    try {
      setLoading(true);
      setError(null);

      const result = await revenueService.deleteTransaction(transactionId);
      
      if (result.success) {
        await fetchRevenueTransactions(); // Refresh transactions
        await fetchRevenueStats(); // Refresh stats
        return result;
      } else {
        throw new Error(result.error || result.message || 'Failed to delete revenue transaction');
      }
    } catch (error) {
      console.error('Delete revenue transaction error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchCODIncome = async () => {
    setRevenueData((prev) => {
      const summary = computeRevenueSummaryFromTransactions(prev.transactions || []);
      return syncRevenueFromTransactions(prev, prev.transactions || [], {
        codPendingIncome: summary.codPendingIncome,
        codRealIncome: summary.codRealIncome,
        codTotalIncome: summary.codTotalIncome,
      });
    });
  };

  const refetchAll = async () => {
    if (scopedCreatorId) {
      await fetchPaymentAccounts();
      return;
    }
    await fetchRevenueStats();
    await fetchPaymentAccounts();
  };

  useEffect(() => {
    refetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when shopkeeper scope changes
  }, [scopedCreatorId]);

  return { 
    revenueData, 
    loading, 
    error, 
    refetch: refetchAll,
    refetchStats: fetchRevenueStats,
    refetchOverview: fetchRevenueOverview,
    refetchTransactions: fetchRevenueTransactions,
    refetchAccounts: fetchPaymentAccounts,
    refetchCODIncome: fetchCODIncome,
    // Payment Account methods
    addPaymentAccount,
    updatePaymentAccount,
    deletePaymentAccount,
    toggleAccountVisibility,
    toggleAccountStatus,
    // Revenue Transaction methods
    addRevenueTransaction,
    updateRevenueTransaction,
    deleteRevenueTransaction
  };
};

export default useRevenue;
